import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/ranking", label: "排行榜" },
  { to: "/guide", label: "入门指南" },
  { to: "/about", label: "关于幻夜志" },
];

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-50">
        <i className="ri-moon-clear-line text-xl"></i>
      </span>
      <span className="font-display text-xl font-bold tracking-wide text-foreground-950">
        幻夜志
      </span>
    </Link>
  );
}

export default function SiteLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <header className="sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/90 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
          <Brand />
          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => {
              const active =
                item.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`rounded-full px-4 py-2 text-sm transition-colors ${
                    active
                      ? "bg-primary-100 text-primary-800 font-medium"
                      : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="hidden items-center gap-3 md:flex">
            <Link
              to="/guide"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
            >
              <i className="ri-compass-3-line"></i>
              找作品
            </Link>
          </div>
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-800 md:hidden"
            aria-label="打开菜单"
          >
            <i className={open ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
          </button>
        </div>
        {open && (
          <nav className="border-t border-background-200/80 bg-background-50 md:hidden">
            <div className="flex flex-col gap-1 px-4 py-3">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="rounded-lg px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/guide"
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
              >
                <i className="ri-compass-3-line"></i>
                找作品
              </Link>
            </div>
          </nav>
        )}
      </header>

      <main className="min-h-[60vh]">
        <Outlet />
      </main>

      <footer className="border-t border-background-200/70 bg-background-100 text-foreground-800">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                <i className="ri-moon-clear-line text-xl"></i>
              </span>
              <span className="font-display text-xl font-bold text-foreground-950">幻夜志</span>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-foreground-600">
              专注工口游戏的推荐、排行榜、评测与神作盘点。我们只收录值得一玩的作品，
              帮你避开雷区，快速锁定下一款心头好。
            </p>
            <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent-700">
              <i className="ri-24-hours-line"></i>
              本站内容面向成年用户
            </p>
          </div>
          <div>
            <h3 className="font-display text-sm font-bold uppercase tracking-wider text-foreground-950">栏目</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li><Link to="/" className="text-foreground-600 hover:text-accent-600">首页</Link></li>
              <li><Link to="/ranking" className="text-foreground-600 hover:text-accent-600">排行榜</Link></li>
              <li><Link to="/guide" className="text-foreground-600 hover:text-accent-600">入门指南</Link></li>
              <li><Link to="/about" className="text-foreground-600 hover:text-accent-600">关于我们</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-display text-sm font-bold uppercase tracking-wider text-foreground-950">常用</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li><a href="/#categories" className="text-foreground-600 hover:text-accent-600">主题分类</a></li>
              <li><a href="/#masterpieces" className="text-foreground-600 hover:text-accent-600">神作盘点</a></li>
              <li><a href="/#reviews" className="text-foreground-600 hover:text-accent-600">最新评测</a></li>
              <li><a href="/#faq" className="text-foreground-600 hover:text-accent-600">常见问题</a></li>
            </ul>
          </div>
          <div>
            <h3 className="font-display text-sm font-bold uppercase tracking-wider text-foreground-950">联系我们</h3>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              欢迎投稿、纠错或推荐你心中的遗珠之作。
            </p>
            <a href="mailto:hello@huanyezhi.example.com" className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-accent-700 hover:text-accent-600">
              <i className="ri-mail-line"></i>
              hello@huanyezhi.example.com
            </a>
            <div className="mt-5 flex gap-3">
              <a href="#" rel="nofollow" aria-label="微博" className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200 text-foreground-600 transition-colors hover:bg-primary-200 hover:text-primary-800"><i className="ri-weibo-line"></i></a>
              <a href="#" rel="nofollow" aria-label="微信公众号" className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200 text-foreground-600 transition-colors hover:bg-primary-200 hover:text-primary-800"><i className="ri-wechat-line"></i></a>
              <a href="#" rel="nofollow" aria-label="哔哩哔哩" className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200 text-foreground-600 transition-colors hover:bg-primary-200 hover:text-primary-800"><i className="ri-bilibili-line"></i></a>
            </div>
          </div>
        </div>
        <div className="border-t border-background-200/70">
          <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-xs text-foreground-500 sm:flex-row md:px-6">
            <p>© {new Date().getFullYear()} 幻夜志 · 本站仅供内容参考，请遵守当地法律法规，理性消费，适度游玩。</p>
            <p>成人向内容 · 请确认你已年满 18 周岁</p>
          </div>
        </div>
      </footer>
    </div>
  );
}